/**
 * Metadados de ajuda contextual para indicadores de saude APS.
 * Fonte: Notas Metodologicas do Cofinanciamento APS (Ministerio da Saude).
 *
 * Cada eixo contem:
 *  - nome, descricao, tipo (boas-praticas | taxa | producao | vinculo)
 *  - classificacao: faixas de score e cores
 *  - indicadores: lista de indicadores com descricao, calculo, codigos, periodo, peso
 */

export interface CodigoInfo {
  codigo: string;
  descricao: string;
}

export interface IndicadorHelp {
  /** Chave do indicador (A, B, C...) */
  chave: string;
  /** Nome curto do indicador */
  nome: string;
  /** Descricao acessivel para gestores */
  descricao: string;
  /** Como e calculado (numerador / denominador) */
  calculo: string;
  /** Periodo de referencia (ex: "ultimos 6 meses") */
  periodo: string;
  /** Peso no score total do eixo */
  peso: number;
  /** Tabelas fonte no banco e-SUS */
  fontes: string[];
  /** Codigos SIGTAP, CIAP, CID, Imunobiologicos utilizados */
  codigos: CodigoInfo[];
  /** Acao recomendada para o gestor quando indicador esta abaixo do esperado */
  acaoRecomendada?: string;
}

export interface ClassificacaoFaixa {
  label: string;
  cor: string;
  faixa: string;
}

export interface EixoHelp {
  /** Slug do eixo (ex: "c2-criancas") */
  eixo: string;
  /** Nome completo do eixo */
  nome: string;
  /** Descricao geral do eixo */
  descricao: string;
  /** Tipo de calculo */
  tipo: 'boas-praticas' | 'taxa' | 'producao' | 'vinculo';
  /** Populacao-alvo */
  populacao: string;
  /** Score total possivel */
  scoreTotal: number;
  /** Faixas de classificacao */
  classificacao: ClassificacaoFaixa[];
  /** Lista de indicadores */
  indicadores: IndicadorHelp[];
  /** Quando true, oculta os selos de score/peso no modal (indicadores de taxa/razao, ex: eSB) */
  ocultarScore?: boolean;
}

const classificacaoPadrao: ClassificacaoFaixa[] = [
  { label: 'Otimo', cor: '#2e7d32', faixa: 'Score acima de 75' },
  { label: 'Bom', cor: '#1565c0', faixa: 'Score entre 50 e 75' },
  { label: 'Suficiente', cor: '#e65100', faixa: 'Score entre 25 e 50' },
  { label: 'Regular', cor: '#c62828', faixa: 'Score abaixo de 25' },
];

const classificacaoC1: ClassificacaoFaixa[] = [
  { label: 'Otimo', cor: '#2e7d32', faixa: '50% a 70% de atendimentos programados' },
  { label: 'Bom', cor: '#1565c0', faixa: '30% a 50% de atendimentos programados' },
  { label: 'Suficiente', cor: '#e65100', faixa: '10% a 30% de atendimentos programados' },
  { label: 'Regular', cor: '#c62828', faixa: 'Abaixo de 10% ou acima de 70%' },
];

export const INDICADORES_HELP: Record<string, EixoHelp> = {
  'c1-mais-acesso': {
    eixo: 'c1-mais-acesso',
    nome: 'C1 - Mais Acesso',
    descricao: 'Avalia o equilibrio entre atendimentos programados (agendados) e espontaneos (demanda livre). O objetivo e que o municipio mantenha entre 50% e 70% de atendimentos programados, indicando que a equipe consegue organizar a agenda mantendo espaco para demanda espontanea.',
    tipo: 'taxa',
    populacao: 'Todos os atendimentos individuais realizados por medicos e enfermeiros no quadrimestre.',
    scoreTotal: 100,
    classificacao: classificacaoC1,
    indicadores: [
      {
        chave: 'A',
        nome: '% Atendimentos Programados',
        descricao: 'Percentual de atendimentos individuais que foram programados (agendados previamente) em relacao ao total de atendimentos no quadrimestre. Atendimentos programados incluem consultas agendadas e cuidado continuado. Atendimentos espontaneos incluem escuta inicial, consulta no dia, urgencia e demanda espontanea.',
        calculo: 'Atendimentos programados (tipos 1, 2, 7) dividido pelo total de atendimentos (programados + espontaneos)',
        periodo: 'Quadrimestre atual (4 meses)',
        peso: 100,
        fontes: ['tb_fat_atendimento_individual', 'tb_dim_tipo_atendimento', 'tb_dim_cbo'],
        codigos: [
          { codigo: 'Tipo 1', descricao: 'Consulta agendada programada / cuidado continuado' },
          { codigo: 'Tipo 2', descricao: 'Consulta agendada' },
          { codigo: 'Tipo 7', descricao: 'Atendimento programado / cuidado continuado' },
          { codigo: 'Tipo 4', descricao: 'Escuta inicial / orientacao (espontanea)' },
          { codigo: 'Tipo 5', descricao: 'Consulta no dia (espontanea)' },
          { codigo: 'Tipo 6', descricao: 'Atendimento de urgencia (espontanea)' },
          { codigo: 'Tipo 8', descricao: 'Demanda espontanea - outras' },
        ],
        acaoRecomendada: 'Revisar agenda das equipes para equilibrar atendimentos programados e espontaneos. Manter entre 50% e 70% de programados.',
      },
    ],
  },

  'c2-criancas': {
    eixo: 'c2-criancas',
    nome: 'C2 - Criancas',
    descricao: 'Acompanha a qualidade da atencao a saude de criancas menores de 2 anos. Avalia se as equipes estao realizando as consultas previstas, o acompanhamento do crescimento, as visitas domiciliares e a vacinacao completa, conforme preconizado pela Politica Nacional de Atencao Integral a Saude da Crianca.',
    tipo: 'boas-praticas',
    populacao: 'Criancas menores de 2 anos, com cadastro ativo e vinculadas a uma equipe de saude.',
    scoreTotal: 100,
    classificacao: classificacaoPadrao,
    indicadores: [
      {
        chave: 'A',
        nome: '1a consulta ate 30 dias de vida',
        descricao: 'Verifica se a crianca realizou sua primeira consulta com medico ou enfermeiro nos primeiros 30 dias de vida. Essa consulta inicial e fundamental para avaliar o estado de saude do recem-nascido, orientar sobre aleitamento materno e identificar possiveis problemas precoces.',
        calculo: 'Criancas com pelo menos 1 atendimento nos primeiros 30 dias apos o nascimento, dividido pelo total de criancas menores de 2 anos',
        periodo: 'Ate 30 dias apos o nascimento',
        peso: 20,
        fontes: ['tb_fat_atendimento_individual', 'tb_dim_cbo'],
        codigos: [
          { codigo: 'CBO 2251xx', descricao: 'Medico clinico' },
          { codigo: 'CBO 2252xx', descricao: 'Medico de familia' },
          { codigo: 'CBO 2231xx', descricao: 'Enfermeiro' },
        ],
        acaoRecomendada: 'Garantir busca ativa de recem-nascidos para primeira consulta nos primeiros 30 dias de vida.',
      },
      {
        chave: 'B',
        nome: '9+ consultas no 1o ano',
        descricao: 'Verifica se a crianca realizou pelo menos 9 consultas com medico ou enfermeiro ate os 2 anos de vida. O acompanhamento regular e essencial para monitorar o desenvolvimento, identificar atrasos e prevenir doencas.',
        calculo: 'Criancas com 9 ou mais consultas distintas ate 2 anos de vida (24 meses), dividido pelo total',
        periodo: 'Ate 2 anos de vida (24 meses)',
        peso: 20,
        fontes: ['tb_fat_atendimento_individual', 'tb_dim_cbo'],
        codigos: [],
        acaoRecomendada: 'Organizar agenda de puericultura com consultas mensais no 1o ano. ACS deve fazer busca ativa de faltosos.',
      },
      {
        chave: 'C',
        nome: '9+ registros antropometricos (12m)',
        descricao: 'Avalia se a crianca teve pelo menos 9 registros de peso e altura nos ultimos 12 meses. A antropometria e fundamental para acompanhar o crescimento e desenvolvimento, identificando precocemente desnutricao ou sobrepeso.',
        calculo: 'Criancas com 9 ou mais registros de peso e altura em datas distintas nos ultimos 12 meses',
        periodo: 'Ultimos 12 meses',
        peso: 20,
        fontes: ['tb_fat_atendimento_individual', 'tb_fat_proced_atend'],
        codigos: [
          { codigo: '0101040024', descricao: 'Avaliacao antropometrica' },
          { codigo: '0101040083', descricao: 'Avaliacao nutricional' },
          { codigo: '0101040075', descricao: 'Calculo de IMC' },
        ],
        acaoRecomendada: 'Garantir registro de peso e altura em TODA consulta de puericultura. Orientar profissionais sobre codigos corretos.',
      },
      {
        chave: 'D',
        nome: 'Visitas ACS com intervalo 30d (12m)',
        descricao: 'Verifica se a crianca recebeu pelo menos 2 visitas do Agente Comunitario de Saude: a primeira nos 30 dias de vida e a segunda ate os 6 meses. As visitas domiciliares sao essenciais para apoiar a familia nos cuidados com o bebe.',
        calculo: '1a visita ACS nos primeiros 30 dias + 2a visita entre 30 dias e 6 meses de vida',
        periodo: 'Ate 6 meses de vida',
        peso: 20,
        fontes: ['tb_fat_visita_domiciliar', 'tb_dim_cbo'],
        codigos: [
          { codigo: 'CBO 515105', descricao: 'Agente Comunitario de Saude (ACS)' },
        ],
        acaoRecomendada: 'Priorizar visitas ACS a recem-nascidos nos primeiros 30 dias e manter acompanhamento mensal.',
      },
      {
        chave: 'E',
        nome: 'Vacinas em dia (Penta/VIP/SCR/Pneumo)',
        descricao: 'Avalia se a crianca esta com o calendario vacinal completo para as vacinas Pentavalente (3 doses), VIP/Polio (3 doses), Triplice Viral - SCR (2 doses) e Pneumococica 10 (2 doses). A vacinacao completa e essencial para prevenir doencas graves na infancia.',
        calculo: 'Criancas com todas as vacinas obrigatorias em dia (Penta >= 3, VIP >= 3, SCR >= 2, Pneumo10 >= 2)',
        periodo: 'Historico completo de vacinacao',
        peso: 20,
        fontes: ['tb_fat_vacinacao', 'tb_fat_vacinacao_vacina', 'tb_dim_imunobiologico'],
        codigos: [
          { codigo: 'Imuno 09, 17, 29, 39, 42, 43, 46, 47, 58', descricao: 'Pentavalente e variantes (3 doses)' },
          { codigo: 'Imuno 22, 29, 43, 58', descricao: 'VIP/Polio Inativada (3 doses)' },
          { codigo: 'Imuno 24, 56', descricao: 'Triplice Viral - SCR (2 doses)' },
          { codigo: 'Imuno 26, 59, 106, 107', descricao: 'Pneumococica 10-valente (2 doses)' },
        ],
        acaoRecomendada: 'Verificar cartao de vacinacao em toda consulta. ACS deve fazer busca ativa de criancas com vacinas atrasadas.',
      },
    ],
  },

  'c3-gestantes': {
    eixo: 'c3-gestantes',
    nome: 'C3 - Gestantes',
    descricao: 'Avalia a qualidade do acompanhamento pre-natal, parto e puerperio. Abrange desde o inicio precoce do pre-natal ate o acompanhamento pos-parto, incluindo consultas, exames, vacinacao e saude bucal da gestante.',
    tipo: 'boas-praticas',
    populacao: 'Gestantes identificadas pelo cadastro individual (st_gestante=1) ou por atendimento com codigos CIAP de gestacao (W78, W79, W81, W84, W85).',
    scoreTotal: 100,
    classificacao: classificacaoPadrao,
    indicadores: [
      {
        chave: 'A',
        nome: 'Inicio precoce pre-natal (ate 12 semanas)',
        descricao: 'Verifica se a gestante iniciou o acompanhamento pre-natal ate a 12a semana de gestacao. O inicio precoce permite identificar e tratar problemas desde o comeco da gravidez, reduzindo riscos para a mae e o bebe.',
        calculo: 'Gestantes com 1a consulta pre-natal ate 12 semanas de gestacao, dividido pelo total de gestantes',
        periodo: 'Ultimos 12 meses',
        peso: 10,
        fontes: ['tb_fat_atendimento_individual', 'tb_dim_cbo'],
        codigos: [
          { codigo: 'CIAP W78', descricao: 'Gravidez' },
          { codigo: 'CIAP W79', descricao: 'Gravidez nao desejada' },
          { codigo: 'CIAP W81', descricao: 'Toxemia gravida / pre-eclampsia' },
          { codigo: 'CIAP W84', descricao: 'Gravidez de alto risco' },
          { codigo: 'CIAP W85', descricao: 'Diabetes gestacional' },
          { codigo: 'CIAP ABP001', descricao: 'Pre-natal' },
        ],
        acaoRecomendada: 'Busca ativa de gestantes no territorio para inicio precoce do pre-natal ate 12 semanas.',
      },
      {
        chave: 'B',
        nome: '7+ consultas na gestacao',
        descricao: 'Verifica se a gestante realizou pelo menos 7 consultas pre-natal com medico ou enfermeiro. O minimo recomendado pelo Ministerio da Saude para um pre-natal adequado e de 7 consultas.',
        calculo: 'Gestantes com 7 ou mais consultas pre-natal distintas',
        periodo: 'Ultimos 12 meses',
        peso: 9,
        fontes: ['tb_fat_atendimento_individual', 'tb_dim_cbo'],
        codigos: [],
        acaoRecomendada: 'Organizar agenda pre-natal com consultas mensais intercalando medico e enfermeiro.',
      },
      {
        chave: 'C',
        nome: '7+ afericoes PA na gestacao',
        descricao: 'Verifica se a gestante teve a pressao arterial aferida em pelo menos 7 consultas durante a gestacao. O monitoramento da PA e crucial para detectar pre-eclampsia e eclampsia.',
        calculo: 'Gestantes com 7 ou mais afericoes de pressao arterial registradas',
        periodo: 'Ultimos 12 meses',
        peso: 9,
        fontes: ['tb_fat_proced_atend', 'tb_dim_cbo'],
        codigos: [
          { codigo: '0301100039', descricao: 'Afericao de pressao arterial' },
          { codigo: 'ABPG033', descricao: 'Afericao de PA (codigo APS)' },
        ],
        acaoRecomendada: 'Garantir registro de PA em todas as consultas pre-natal. Orientar equipe sobre codigos corretos.',
      },
      {
        chave: 'D',
        nome: 'Antropometria na gestacao',
        descricao: 'Verifica se a gestante teve 7 ou mais registros antropometricos (peso e altura em consultas distintas) durante a gestacao, conforme metodologia do Ministerio da Saude. O acompanhamento do ganho de peso gestacional e importante para avaliar o estado nutricional da mae e o crescimento fetal.',
        calculo: 'Gestantes com 7 ou mais registros de peso e altura em datas distintas',
        periodo: 'Ultimos 12 meses',
        peso: 9,
        fontes: ['tb_fat_atendimento_individual', 'tb_fat_proced_atend'],
        codigos: [
          { codigo: '0101040024', descricao: 'Avaliacao antropometrica' },
        ],
        acaoRecomendada: 'Registrar peso e altura em toda consulta pre-natal com codigo de avaliacao antropometrica.',
      },
      {
        chave: 'E',
        nome: '3+ visitas ACS na gestacao',
        descricao: 'Verifica se a gestante recebeu pelo menos 3 visitas do Agente Comunitario de Saude durante a gestacao. As visitas domiciliares sao essenciais para apoio, orientacao e busca ativa.',
        calculo: 'Gestantes com 3 ou mais visitas do ACS registradas',
        periodo: 'Ultimos 12 meses',
        peso: 9,
        fontes: ['tb_fat_visita_domiciliar', 'tb_dim_cbo'],
        codigos: [
          { codigo: 'CBO 515105', descricao: 'Agente Comunitario de Saude (ACS)' },
        ],
        acaoRecomendada: 'Incluir gestantes como prioridade na microarea do ACS. Garantir pelo menos 3 visitas durante a gestacao.',
      },
      {
        chave: 'F',
        nome: 'dTpa na gestacao',
        descricao: 'Verifica se a gestante recebeu a vacina dTpa (triplice bacteriana acelular do adulto) durante a gestacao. A dTpa protege o recem-nascido contra coqueluche nos primeiros meses de vida.',
        calculo: 'Gestantes vacinadas com dTpa durante a gestacao',
        periodo: 'Ultimos 12 meses',
        peso: 9,
        fontes: ['tb_fat_vacinacao', 'tb_dim_imunobiologico'],
        codigos: [
          { codigo: 'Imuno 57', descricao: 'dTpa adulto (triplice bacteriana acelular)' },
        ],
        acaoRecomendada: 'Garantir disponibilidade da vacina dTpa na UBS e orientar vacinacao a partir da 20a semana.',
      },
      {
        chave: 'G',
        nome: 'Exames 1o trimestre (sifilis, HIV, hepB, hepC)',
        descricao: 'Verifica se a gestante realizou os exames de rastreio obrigatorios no primeiro trimestre: sifilis, HIV, hepatite B e hepatite C. A deteccao precoce dessas infeccoes permite tratamento oportuno e prevencao da transmissao vertical.',
        calculo: 'Gestantes com pelo menos um exame de rastreio solicitado no primeiro trimestre',
        periodo: 'Ultimos 12 meses',
        peso: 9,
        fontes: ['tb_fat_atd_ind_procedimentos', 'tb_dim_procedimento'],
        codigos: [
          { codigo: '0202031110', descricao: 'VDRL para sifilis' },
          { codigo: '0202031179', descricao: 'Teste rapido para sifilis' },
          { codigo: '0214010058', descricao: 'Teste rapido para hepatite B' },
          { codigo: '0214010066', descricao: 'Teste rapido para hepatite C' },
          { codigo: '0202030300', descricao: 'Pesquisa de anticorpos anti-HIV' },
          { codigo: '0214010040', descricao: 'Teste rapido para HIV' },
          { codigo: '0202060373', descricao: 'Pesquisa de HBsAg' },
          { codigo: '0202060381', descricao: 'Pesquisa de anti-HCV' },
          { codigo: 'ABEX019', descricao: 'Teste rapido sifilis (APS)' },
          { codigo: 'ABEX005', descricao: 'Teste rapido HIV (APS)' },
          { codigo: 'ABEX006', descricao: 'Teste rapido hepatite B (APS)' },
          { codigo: 'ABEX012', descricao: 'Teste rapido hepatite C (APS)' },
        ],
        acaoRecomendada: 'Solicitar todos os exames de rastreio na 1a consulta pre-natal. Manter insumos de testes rapidos disponiveis.',
      },
      {
        chave: 'H',
        nome: 'Exames 3o trimestre (sifilis, HIV)',
        descricao: 'Verifica se a gestante repetiu os exames de sifilis e HIV no terceiro trimestre (a partir de 27 semanas). A repeticao e importante para detectar infeccoes adquiridas durante a gestacao.',
        calculo: 'Gestantes com exames de sifilis e HIV a partir da 27a semana',
        periodo: 'Ultimos 12 meses',
        peso: 9,
        fontes: ['tb_fat_atd_ind_procedimentos', 'tb_dim_procedimento'],
        codigos: [
          { codigo: '0202031110', descricao: 'VDRL para sifilis' },
          { codigo: '0202030300', descricao: 'Pesquisa de anticorpos anti-HIV' },
        ],
        acaoRecomendada: 'Repetir testes de sifilis e HIV no 3o trimestre (a partir de 27 semanas). Incluir no protocolo pre-natal.',
      },
      {
        chave: 'I',
        nome: 'Consulta puerperal medico/enfermeiro',
        descricao: 'Verifica se a puerpera realizou consulta medica ou de enfermagem no puerperio. A consulta puerperal e essencial para avaliar a recuperacao da mae, amamentacao e saude mental materna.',
        calculo: 'Puerperas com consulta registrada com CIAP de puerperio (W18, W96)',
        periodo: 'Ultimos 12 meses',
        peso: 9,
        fontes: ['tb_fat_atendimento_individual', 'tb_dim_cbo'],
        codigos: [
          { codigo: 'CIAP W18', descricao: 'Puerperio' },
          { codigo: 'CIAP W96', descricao: 'Complicacoes pos-parto' },
        ],
        acaoRecomendada: 'Agendar consulta puerperal na alta hospitalar. Busca ativa de puerperas que nao retornaram.',
      },
      {
        chave: 'J',
        nome: 'Visita puerperal ACS',
        descricao: 'Verifica se a puerpera recebeu visita domiciliar do ACS no puerperio. A visita puerperal e importante para apoiar a familia, orientar sobre cuidados com o recem-nascido e identificar sinais de alerta.',
        calculo: 'Puerperas com visita domiciliar registrada com desfecho "puerpera"',
        periodo: 'Ultimos 12 meses',
        peso: 9,
        fontes: ['tb_fat_visita_domiciliar', 'tb_dim_cbo'],
        codigos: [
          { codigo: 'Desfecho 5', descricao: 'Puerpera (na ficha de visita domiciliar)' },
        ],
        acaoRecomendada: 'ACS deve visitar puerpera na 1a semana apos o parto. Registrar desfecho "puerpera" na ficha.',
      },
      {
        chave: 'K',
        nome: 'Saude bucal na gestacao',
        descricao: 'Verifica se a gestante realizou pelo menos um atendimento odontologico durante a gestacao. A saude bucal da gestante e importante pois infeccoes dentarias podem causar complicacoes na gravidez.',
        calculo: 'Gestantes com atendimento odontologico registrado por cirurgiao-dentista ou TSB',
        periodo: 'Ultimos 12 meses',
        peso: 9,
        fontes: ['tb_fat_atendimento_odonto', 'tb_dim_cbo'],
        codigos: [
          { codigo: 'CBO 2232xx', descricao: 'Cirurgiao-Dentista' },
          { codigo: 'CBO 3224xx', descricao: 'Tecnico em Saude Bucal (TSB)' },
        ],
        acaoRecomendada: 'Garantir agendamento odontologico para toda gestante. Integrar eSB no fluxo de pre-natal.',
      },
    ],
  },

  'c4-diabeticos': {
    eixo: 'c4-diabeticos',
    nome: 'C4 - Diabeticos',
    descricao: 'Avalia a qualidade do cuidado de pessoas com diabetes mellitus. Monitora se as equipes estao realizando consultas regulares, aferindo pressao arterial, acompanhando peso e altura, realizando visitas domiciliares, solicitando hemoglobina glicada e avaliando o pe diabetico.',
    tipo: 'boas-praticas',
    populacao: 'Cidadaos com diagnostico de diabetes (CIAP T89/T90/ABP006 ou CID E10-E14), com cadastro ativo.',
    scoreTotal: 100,
    classificacao: classificacaoPadrao,
    indicadores: [
      {
        chave: 'A',
        nome: 'Consulta medico/enfermeiro (6m)',
        descricao: 'Verifica se o paciente diabetico realizou pelo menos uma consulta com medico ou enfermeiro nos ultimos 6 meses. O acompanhamento regular e essencial para ajustar o tratamento e prevenir complicacoes.',
        calculo: 'Diabeticos com consulta medica/enfermagem nos ultimos 6 meses',
        periodo: 'Ultimos 6 meses',
        peso: 20,
        fontes: ['tb_fat_atendimento_individual', 'tb_dim_cbo'],
        codigos: [
          { codigo: 'CIAP T89', descricao: 'Diabetes insulino-dependente' },
          { codigo: 'CIAP T90', descricao: 'Diabetes nao insulino-dependente' },
          { codigo: 'CIAP ABP006', descricao: 'Diabetes mellitus' },
          { codigo: 'CID E10-E14', descricao: 'Diabetes mellitus (tipos 1, 2 e outros)' },
        ],
        acaoRecomendada: 'Garantir consulta semestral para todos os diabeticos. Busca ativa de faltosos pelo ACS.',
      },
      {
        chave: 'B',
        nome: 'Afericao de pressao arterial (6m)',
        descricao: 'Verifica se o paciente diabetico teve a pressao arterial aferida nos ultimos 6 meses. Diabeticos tem maior risco cardiovascular e o controle da PA e parte fundamental do tratamento.',
        calculo: 'Diabeticos com afericao de PA registrada nos ultimos 6 meses',
        periodo: 'Ultimos 6 meses',
        peso: 15,
        fontes: ['tb_fat_proced_atend', 'tb_dim_cbo'],
        codigos: [
          { codigo: '0301100039', descricao: 'Afericao de pressao arterial' },
          { codigo: 'ABPG033', descricao: 'Afericao de PA (codigo APS)' },
        ],
        acaoRecomendada: 'Aferir PA em toda consulta de diabetico. Registrar com codigo correto no PEC.',
      },
      {
        chave: 'C',
        nome: 'Peso e altura (12m)',
        descricao: 'Verifica se o paciente diabetico teve peso e altura registrados nos ultimos 12 meses. O controle do peso e fundamental no manejo do diabetes, especialmente do tipo 2.',
        calculo: 'Diabeticos com registro de peso e altura nos ultimos 12 meses',
        periodo: 'Ultimos 12 meses',
        peso: 15,
        fontes: ['tb_fat_atendimento_individual', 'tb_fat_proced_atend'],
        codigos: [
          { codigo: '0101040024', descricao: 'Avaliacao antropometrica' },
        ],
        acaoRecomendada: 'Registrar peso e altura anualmente. Orientar equipe sobre importancia da antropometria no manejo do DM.',
      },
      {
        chave: 'D',
        nome: 'Visitas ACS com intervalo 30d (12m)',
        descricao: 'Verifica se o paciente diabetico recebeu pelo menos 2 visitas do ACS com intervalo minimo de 30 dias entre elas nos ultimos 12 meses. As visitas regulares promovem adesao ao tratamento.',
        calculo: '2+ visitas ACS com pelo menos 30 dias de intervalo nos ultimos 12 meses',
        periodo: 'Ultimos 12 meses',
        peso: 20,
        fontes: ['tb_fat_visita_domiciliar', 'tb_dim_cbo'],
        codigos: [
          { codigo: 'CBO 515105', descricao: 'Agente Comunitario de Saude (ACS)' },
        ],
        acaoRecomendada: 'Programar visitas ACS a diabeticos com intervalo minimo de 30 dias. Priorizar pacientes descompensados.',
      },
      {
        chave: 'E',
        nome: 'Hemoglobina glicada solicitada (12m)',
        descricao: 'Verifica se foi solicitado ou avaliado o exame de hemoglobina glicada (HbA1c) nos ultimos 12 meses. A HbA1c e o principal exame para avaliar o controle glicemico a longo prazo.',
        calculo: 'Diabeticos com HbA1c solicitada ou avaliada nos ultimos 12 meses',
        periodo: 'Ultimos 12 meses',
        peso: 15,
        fontes: ['tb_fat_atd_ind_procedimentos', 'tb_dim_procedimento'],
        codigos: [
          { codigo: 'ABEX008', descricao: 'Hemoglobina glicada (HbA1c) - codigo APS' },
          { codigo: '0202010503', descricao: 'Dosagem de hemoglobina glicada' },
        ],
        acaoRecomendada: 'Solicitar HbA1c a cada 6-12 meses. Incluir no protocolo de consulta de rotina do diabetico.',
      },
      {
        chave: 'F',
        nome: 'Avaliacao do pe diabetico (12m)',
        descricao: 'Verifica se o paciente diabetico teve avaliacao do pe diabetico nos ultimos 12 meses, realizada por medico, enfermeiro, farmaceutico, fisioterapeuta, terapeuta ocupacional ou tecnico de enfermagem. O pe diabetico e uma complicacao grave que pode levar a amputacao se nao identificada e tratada precocemente.',
        calculo: 'Diabeticos com procedimento de avaliacao do pe diabetico nos ultimos 12 meses',
        periodo: 'Ultimos 12 meses',
        peso: 15,
        fontes: ['tb_fat_proced_atend', 'tb_dim_cbo'],
        codigos: [
          { codigo: '0301040095', descricao: 'Avaliacao do pe diabetico' },
          { codigo: 'CBO 2234%', descricao: 'Farmaceutico' },
          { codigo: 'CBO 2236%', descricao: 'Fisioterapeuta' },
          { codigo: 'CBO 2239%', descricao: 'Terapeuta ocupacional' },
        ],
        acaoRecomendada: 'Capacitar equipe para avaliacao do pe diabetico. Incluir exame anual no protocolo de DM.',
      },
    ],
  },

  'c5-hipertensos': {
    eixo: 'c5-hipertensos',
    nome: 'C5 - Hipertensos',
    descricao: 'Avalia a qualidade do cuidado de pessoas com hipertensao arterial. Monitora consultas regulares, afericao de pressao arterial, acompanhamento antropometrico e visitas domiciliares do ACS.',
    tipo: 'boas-praticas',
    populacao: 'Cidadaos com diagnostico de hipertensao arterial (CIAP K86/K87/ABP005 ou CID I10-I15), com cadastro ativo.',
    scoreTotal: 100,
    classificacao: classificacaoPadrao,
    indicadores: [
      {
        chave: 'A',
        nome: 'Consulta medico/enfermeiro (6m)',
        descricao: 'Verifica se o paciente hipertenso realizou consulta com medico ou enfermeiro nos ultimos 6 meses. O acompanhamento regular e essencial para controle da pressao e prevencao de eventos cardiovasculares.',
        calculo: 'Hipertensos com consulta nos ultimos 6 meses',
        periodo: 'Ultimos 6 meses',
        peso: 25,
        fontes: ['tb_fat_atendimento_individual', 'tb_dim_cbo'],
        codigos: [
          { codigo: 'CIAP K86', descricao: 'Hipertensao sem complicacao' },
          { codigo: 'CIAP K87', descricao: 'Hipertensao com complicacao' },
          { codigo: 'CIAP ABP005', descricao: 'Hipertensao arterial' },
          { codigo: 'CID I10-I15', descricao: 'Doencas hipertensivas' },
        ],
        acaoRecomendada: 'Garantir consulta semestral para hipertensos. Organizar grupos de HIPERDIA na UBS.',
      },
      {
        chave: 'B',
        nome: 'Afericao de pressao arterial (6m)',
        descricao: 'Verifica se o paciente hipertenso teve a pressao arterial aferida nos ultimos 6 meses. A afericao regular e essencial para avaliar a eficacia do tratamento.',
        calculo: 'Hipertensos com afericao de PA nos ultimos 6 meses',
        periodo: 'Ultimos 6 meses',
        peso: 25,
        fontes: ['tb_fat_proced_atend', 'tb_dim_cbo'],
        codigos: [
          { codigo: '0301100039', descricao: 'Afericao de pressao arterial' },
          { codigo: 'ABPG033', descricao: 'Afericao de PA (codigo APS)' },
        ],
        acaoRecomendada: 'Aferir PA em toda consulta de hipertenso. Registrar corretamente no e-SUS PEC.',
      },
      {
        chave: 'C',
        nome: 'Peso e altura (12m)',
        descricao: 'Verifica se o paciente hipertenso teve peso e altura registrados nos ultimos 12 meses. O controle do peso e uma das estrategias nao medicamentosas para o controle da hipertensao.',
        calculo: 'Hipertensos com registro de peso e altura nos ultimos 12 meses',
        periodo: 'Ultimos 12 meses',
        peso: 25,
        fontes: ['tb_fat_atendimento_individual', 'tb_fat_proced_atend'],
        codigos: [],
        acaoRecomendada: 'Registrar peso e altura anualmente. Orientar sobre controle de peso como estrategia nao medicamentosa.',
      },
      {
        chave: 'D',
        nome: 'Visitas ACS com intervalo 30d (12m)',
        descricao: 'Verifica se o paciente hipertenso recebeu pelo menos 2 visitas do ACS com intervalo minimo de 30 dias nos ultimos 12 meses.',
        calculo: '2+ visitas ACS com pelo menos 30 dias de intervalo nos ultimos 12 meses',
        periodo: 'Ultimos 12 meses',
        peso: 25,
        fontes: ['tb_fat_visita_domiciliar', 'tb_dim_cbo'],
        codigos: [
          { codigo: 'CBO 515105', descricao: 'Agente Comunitario de Saude (ACS)' },
        ],
        acaoRecomendada: 'Programar visitas ACS mensais a hipertensos. Monitorar adesao ao tratamento.',
      },
    ],
  },

  'c6-idosos': {
    eixo: 'c6-idosos',
    nome: 'C6 - Idosos',
    descricao: 'Avalia a qualidade da atencao a saude da pessoa idosa (60 anos ou mais). Monitora consultas medicas, acompanhamento antropometrico, visitas domiciliares e vacinacao contra influenza.',
    tipo: 'boas-praticas',
    populacao: 'Cidadaos com 60 anos ou mais, com cadastro ativo e vinculados a uma equipe de saude.',
    scoreTotal: 100,
    classificacao: classificacaoPadrao,
    indicadores: [
      {
        chave: 'A',
        nome: 'Consulta medico/enfermeiro (12m)',
        descricao: 'Verifica se o idoso realizou pelo menos uma consulta com medico ou enfermeiro nos ultimos 12 meses. O acompanhamento regular e essencial para prevencao e manejo de doencas cronicas comuns na terceira idade.',
        calculo: 'Idosos com consulta nos ultimos 12 meses',
        periodo: 'Ultimos 12 meses',
        peso: 25,
        fontes: ['tb_fat_atendimento_individual', 'tb_dim_cbo'],
        codigos: [],
        acaoRecomendada: 'Garantir consulta anual para idosos. Priorizar os que nao consultaram ha mais de 12 meses.',
      },
      {
        chave: 'B',
        nome: 'Peso e altura (12m)',
        descricao: 'Verifica se o idoso teve peso e altura registrados nos ultimos 12 meses. O acompanhamento do estado nutricional e importante para prevenir desnutricao e sarcopenia.',
        calculo: 'Idosos com registro de peso e altura nos ultimos 12 meses',
        periodo: 'Ultimos 12 meses',
        peso: 25,
        fontes: ['tb_fat_atendimento_individual', 'tb_fat_proced_atend'],
        codigos: [],
        acaoRecomendada: 'Registrar peso e altura em consultas de idosos. Monitorar risco de desnutricao e sarcopenia.',
      },
      {
        chave: 'C',
        nome: 'Visitas ACS com intervalo 30d (12m)',
        descricao: 'Verifica se o idoso recebeu pelo menos 2 visitas do ACS com intervalo minimo de 30 dias nos ultimos 12 meses.',
        calculo: '2+ visitas ACS com pelo menos 30 dias de intervalo nos ultimos 12 meses',
        periodo: 'Ultimos 12 meses',
        peso: 25,
        fontes: ['tb_fat_visita_domiciliar', 'tb_dim_cbo'],
        codigos: [
          { codigo: 'CBO 515105', descricao: 'Agente Comunitario de Saude (ACS)' },
        ],
        acaoRecomendada: 'Priorizar visitas ACS a idosos, especialmente acamados e com mobilidade reduzida.',
      },
      {
        chave: 'D',
        nome: 'Vacina influenza (12m)',
        descricao: 'Verifica se o idoso recebeu a vacina contra influenza (gripe) nos ultimos 12 meses. A vacinacao anual contra gripe e fundamental para prevenir complicacoes respiratorias graves em idosos.',
        calculo: 'Idosos vacinados contra influenza nos ultimos 12 meses',
        periodo: 'Ultimos 12 meses',
        peso: 25,
        fontes: ['tb_fat_vacinacao', 'tb_dim_imunobiologico'],
        codigos: [
          { codigo: 'Imuno 33', descricao: 'Influenza Trivalente' },
          { codigo: 'Imuno 77', descricao: 'Influenza Tetravalente' },
        ],
        acaoRecomendada: 'Intensificar campanha de vacinacao contra gripe para idosos. ACS deve fazer busca ativa durante campanhas.',
      },
    ],
  },

  'c7-mulheres': {
    eixo: 'c7-mulheres',
    nome: 'C7 - Saude da Mulher',
    descricao: 'Avalia acoes de rastreamento e prevencao voltadas para a saude da mulher, incluindo prevencao de cancer de colo de utero, vacinacao contra HPV, saude sexual e reprodutiva, e rastreamento de cancer de mama.',
    tipo: 'boas-praticas',
    populacao: 'Mulheres de 9 a 69 anos, com cadastro ativo e vinculadas a uma equipe de saude. Cada indicador tem sua faixa etaria especifica.',
    scoreTotal: 100,
    classificacao: classificacaoPadrao,
    indicadores: [
      {
        chave: 'A',
        nome: 'Citopatologico (36m) - 25 a 64 anos',
        descricao: 'Verifica se a mulher de 25 a 64 anos realizou exame citopatologico (Papanicolaou) nos ultimos 36 meses. O exame e o principal metodo de rastreamento do cancer de colo de utero.',
        calculo: 'Mulheres de 25 a 64 anos com citopatologico nos ultimos 3 anos',
        periodo: 'Ultimos 36 meses',
        peso: 20,
        fontes: ['tb_fat_proced_atend', 'tb_fat_atd_ind_procedimentos', 'tb_dim_procedimento'],
        codigos: [
          { codigo: '0201020033', descricao: 'Coleta de material para exame citopatologico' },
          { codigo: '0203010086', descricao: 'Exame citopatologico cervico-vaginal/microflora' },
          { codigo: '0203010019', descricao: 'Exame citopatologico cervico-vaginal' },
          { codigo: 'ABEX001', descricao: 'Citopatologico (codigo APS)' },
          { codigo: 'ABP022', descricao: 'Rastreamento de cancer de colo uterino' },
        ],
        acaoRecomendada: 'Organizar mutiroes de coleta de citopatologico. Busca ativa de mulheres com exame atrasado.',
      },
      {
        chave: 'B',
        nome: 'HPV 1+ dose - 9 a 14 anos',
        descricao: 'Verifica se a menina de 9 a 14 anos recebeu pelo menos 1 dose da vacina contra HPV. A vacina previne a infeccao pelo papilomavirus humano, principal causador do cancer de colo de utero.',
        calculo: 'Meninas de 9 a 14 anos com pelo menos 1 dose de HPV',
        periodo: 'Historico completo',
        peso: 30,
        fontes: ['tb_fat_vacinacao', 'tb_dim_imunobiologico'],
        codigos: [
          { codigo: 'Imuno 67', descricao: 'HPV Quadrivalente' },
          { codigo: 'Imuno 93', descricao: 'HPV Bivalente/Nonavalente' },
        ],
        acaoRecomendada: 'Campanha de vacinacao HPV nas escolas. Sensibilizar familias sobre importancia da vacina.',
      },
      {
        chave: 'C',
        nome: 'Saude sexual/reprodutiva (12m) - 14 a 69 anos',
        descricao: 'Verifica se a mulher de 14 a 69 anos teve atendimento relacionado a saude sexual e reprodutiva nos ultimos 12 meses. Inclui consultas de planejamento familiar, IST, contracepcao e saude ginecologica.',
        calculo: 'Mulheres de 14 a 69 anos com atendimento de saude sexual/reprodutiva nos ultimos 12 meses',
        periodo: 'Ultimos 12 meses',
        peso: 30,
        fontes: ['tb_fat_atendimento_individual'],
        codigos: [
          { codigo: 'CIAP W10-W15', descricao: 'Contracepcao e planejamento familiar' },
          { codigo: 'CIAP X01-X13', descricao: 'Queixas ginecologicas' },
          { codigo: 'CID Z30', descricao: 'Aconselhamento sobre contracepcao' },
          { codigo: 'CID N80-N97', descricao: 'Transtornos ginecologicos' },
        ],
        acaoRecomendada: 'Ampliar oferta de planejamento familiar e saude sexual. Incluir tema nas atividades de grupo.',
      },
      {
        chave: 'D',
        nome: 'Mamografia (24m) - 50 a 69 anos',
        descricao: 'Verifica se a mulher de 50 a 69 anos realizou mamografia nos ultimos 24 meses. A mamografia de rastreamento e o principal exame para deteccao precoce do cancer de mama.',
        calculo: 'Mulheres de 50 a 69 anos com mamografia nos ultimos 2 anos',
        periodo: 'Ultimos 24 meses',
        peso: 20,
        fontes: ['tb_fat_atd_ind_procedimentos', 'tb_dim_procedimento'],
        codigos: [
          { codigo: '0204030030', descricao: 'Mamografia' },
          { codigo: '0204030188', descricao: 'Mamografia Bilateral' },
          { codigo: 'ABP023', descricao: 'Mamografia bilateral de rastreamento (codigo APS)' },
        ],
        acaoRecomendada: 'Garantir acesso a mamografia para mulheres 50-69 anos. Busca ativa das que nao realizaram nos ultimos 2 anos.',
      },
    ],
  },

  'odonto': {
    eixo: 'odonto',
    nome: 'Saude Bucal (B1-B6)',
    descricao: 'Indicadores de desempenho das Equipes de Saude Bucal (eSB), conforme Notas Metodologicas B1-B6 (revisao 2024/2026). Cada indicador tem sua propria faixa de classificacao; alguns sao razao (B1, B4), um e menor-melhor (B3, exodontia) e os demais sao percentuais.',
    tipo: 'taxa',
    populacao: 'B1 e B4: populacao vinculada as equipes eSF/eAP de referencia (regra NT30). Demais: procedimentos realizados pela eSB (CD/TSB).',
    scoreTotal: 100,
    classificacao: [
      { label: 'Otimo', cor: '#2e7d32', faixa: 'Cada indicador segue a faixa da sua nota (ver detalhe do indicador)' },
      { label: 'Bom', cor: '#1565c0', faixa: 'Faixa especifica por indicador' },
      { label: 'Suficiente', cor: '#e65100', faixa: 'Faixa especifica por indicador' },
      { label: 'Regular', cor: '#c62828', faixa: 'Faixa especifica por indicador' },
    ],
    indicadores: [
      {
        chave: 'A',
        nome: 'B1 - 1a consulta programatica (razao)',
        descricao: 'Mede o acesso da populacao a saude bucal por meio da primeira consulta odontologica programatica (com exame clinico, diagnostico e plano preventivo-terapeutico) realizada pelo cirurgiao-dentista, em relacao a populacao vinculada as eSF/eAP de referencia da eSB.',
        calculo: 'Cidadaos com 1a consulta programatica (CD) / populacao vinculada eSF-eAP',
        periodo: 'Quadrimestre movel (4 meses)',
        peso: 17,
        fontes: ['tb_fat_atendimento_odonto'],
        codigos: [
          { codigo: 'Tipo consulta 1', descricao: 'Primeira consulta odontologica programatica (validar via Query 0)' },
        ],
      },
      {
        chave: 'B',
        nome: 'B2 - Tratamento concluido (%)',
        descricao: 'Mede a resolutividade da eSB: proporcao de tratamentos odontologicos concluidos (conduta "Tratamento concluido") em relacao as primeiras consultas odontologicas programaticas realizadas pelo cirurgiao-dentista.',
        calculo: 'Tratamentos concluidos (CD) / 1as consultas programaticas (CD) x 100',
        periodo: 'Quadrimestre movel (4 meses)',
        peso: 17,
        fontes: ['tb_fat_atendimento_odonto'],
        codigos: [],
      },
      {
        chave: 'C',
        nome: 'B3 - Taxa de exodontia (%)',
        descricao: 'Mede a proporcao de exodontias de dentes permanentes sobre o total de procedimentos individuais preventivos, curativos e exodontias da eSB. Indica se a equipe atua no inicio da doenca, com menos procedimentos mutiladores (menor-melhor).',
        calculo: 'Exodontias (CD) / procedimentos preventivos+curativos+exodontias (CD+TSB) x 100',
        periodo: 'Quadrimestre movel (4 meses)',
        peso: 16,
        fontes: ['tb_fat_atendimento_odonto'],
        codigos: [
          { codigo: '0414020138', descricao: 'Exodontia de dente permanente' },
          { codigo: '0414020146', descricao: 'Exodontia multipla com alveoloplastia' },
        ],
      },
      {
        chave: 'D',
        nome: 'B4 - Escovacao supervisionada 6-12a (razao)',
        descricao: 'Mede a proporcao de criancas de 6 a 12 anos que participaram da acao coletiva de escovacao dental supervisionada (registro MIAC) em relacao a populacao de 6 a 12 anos vinculada as eSF/eAP de referencia da eSB.',
        calculo: 'Criancas 6-12 com escovacao supervisionada (Pratica 04) / populacao 6-12 vinculada',
        periodo: 'Quadrimestre movel (4 meses)',
        peso: 17,
        fontes: ['tb_fat_atvdd_coletiva_part', 'tb_fat_atividade_coletiva'],
        codigos: [
          { codigo: 'Pratica em Saude 04', descricao: 'Escovacao dental supervisionada (ds_filtro_pratica_em_saude; validar via Query 0)' },
        ],
      },
      {
        chave: 'E',
        nome: 'B5 - Procedimentos preventivos (%)',
        descricao: 'Mede a proporcao de procedimentos odontologicos individuais preventivos sobre o total de procedimentos odontologicos individuais da eSB. Indica se a equipe adota um modelo de atencao promotor de saude, menos curativista.',
        calculo: 'Procedimentos preventivos / total de procedimentos individuais x 100 (listas por CBO)',
        periodo: 'Quadrimestre movel (4 meses)',
        peso: 17,
        fontes: ['tb_fat_atendimento_odonto'],
        codigos: [
          { codigo: '0101020058', descricao: 'Aplicacao de cariostatico' },
          { codigo: '0101020066', descricao: 'Aplicacao de selante' },
          { codigo: '0101020074', descricao: 'Aplicacao topica de fluor' },
          { codigo: '0101020082', descricao: 'Evidenciacao de placa bacteriana' },
          { codigo: '0101020104', descricao: 'Orientacao de higiene bucal' },
          { codigo: '0101020120', descricao: 'Orientacao de higienizacao de proteses' },
          { codigo: '0307030040', descricao: 'Profilaxia / remocao da placa bacteriana' },
        ],
      },
      {
        chave: 'F',
        nome: 'B6 - TRA/ART (%)',
        descricao: 'Mede a proporcao de procedimentos de Tratamento Restaurador Atraumatico (ART) sobre o total de procedimentos restauradores da eSB, indicando a adocao de tecnica minimamente invasiva pelo cirurgiao-dentista.',
        calculo: 'Procedimentos TRA/ART (CD) / procedimentos restauradores (CD) x 100',
        periodo: 'Quadrimestre movel (4 meses)',
        peso: 16,
        fontes: ['tb_fat_atendimento_odonto'],
        codigos: [
          { codigo: '0307010074', descricao: 'Tratamento restaurador atraumatico (TRA/ART)' },
        ],
      },
    ],
  },

  'producao': {
    eixo: 'producao',
    nome: 'Producao Geral',
    descricao: 'Painel de producao das equipes de saude. Apresenta a contagem total de atendimentos individuais e odontologicos nos ultimos 12 meses, permitindo acompanhar o volume de atividade das equipes.',
    tipo: 'producao',
    populacao: 'Todos os atendimentos individuais e odontologicos registrados no e-SUS nos ultimos 12 meses.',
    scoreTotal: 100,
    classificacao: classificacaoPadrao,
    indicadores: [
      {
        chave: 'A',
        nome: 'Total atendimentos individuais',
        descricao: 'Contagem total de atendimentos individuais realizados pelas equipes nos ultimos 12 meses.',
        calculo: 'Soma de todos os atendimentos individuais',
        periodo: 'Ultimos 12 meses',
        peso: 25,
        fontes: ['tb_fat_atendimento_individual'],
        codigos: [],
      },
      {
        chave: 'B',
        nome: 'Atendimentos programados',
        descricao: 'Total de atendimentos programados (agendados) realizados nos ultimos 12 meses.',
        calculo: 'Soma de atendimentos tipo 1, 2, 7',
        periodo: 'Ultimos 12 meses',
        peso: 25,
        fontes: ['tb_fat_atendimento_individual'],
        codigos: [],
      },
      {
        chave: 'C',
        nome: 'Atendimentos espontaneos',
        descricao: 'Total de atendimentos espontaneos (demanda livre) realizados nos ultimos 12 meses.',
        calculo: 'Soma de atendimentos tipo 4, 5, 6, 8',
        periodo: 'Ultimos 12 meses',
        peso: 25,
        fontes: ['tb_fat_atendimento_individual'],
        codigos: [],
      },
      {
        chave: 'D',
        nome: 'Atendimentos odontologicos',
        descricao: 'Total de atendimentos odontologicos realizados nos ultimos 12 meses.',
        calculo: 'Soma de todos os atendimentos odontologicos',
        periodo: 'Ultimos 12 meses',
        peso: 25,
        fontes: ['tb_fat_atendimento_odonto'],
        codigos: [],
      },
    ],
  },

  'nt30-vinculo': {
    eixo: 'nt30-vinculo',
    nome: 'NT30 - Vinculo e Acompanhamento Territorial',
    descricao: 'Baseado na Nota Tecnica 30/2025 do Ministerio da Saude. Avalia o vinculo territorial em duas dimensoes: Cadastro (30%) — existencia de Ficha de Cadastro Individual (MICI) ativa; e Acompanhamento (70%) — contatos assistenciais nos ultimos 12 meses de qualquer fonte (consultas, odonto, visitas, procedimentos, atividades coletivas, vacinacao). Filtrado para equipes eSF (tipo 70) e eAP (tipo 76).',
    tipo: 'vinculo',
    populacao: 'Cidadaos com cadastro ativo vinculados a equipes eSF/eAP (tipos CNES 70 e 76).',
    scoreTotal: 100,
    classificacao: classificacaoPadrao,
    indicadores: [
      {
        chave: 'Cadastro',
        nome: 'Cadastro Individual (MICI)',
        descricao: 'Dimensao Cadastro (peso 30%). Verifica se o cidadao possui Ficha de Cadastro Individual (MICI) ativa e nao inativada no e-SUS. A NT30 tambem preve bonus para cadastro domiciliar (MICDT), mas o percentual base e de cidadaos com MICI.',
        calculo: 'Cidadaos com FCI ativa / total vinculados a eSF/eAP',
        periodo: 'Cadastro vigente',
        peso: 30,
        fontes: ['tb_fat_cidadao_pec', 'tb_cidadao', 'tb_fat_cad_individual'],
        codigos: [],
      },
      {
        chave: 'Acompanhamento',
        nome: 'Acompanhamento (contatos 12m)',
        descricao: 'Dimensao Acompanhamento (peso 70%). Verifica se o cidadao teve pelo menos 1 contato assistencial nos ultimos 12 meses, de qualquer fonte: atendimento individual, odontologico, visita domiciliar, atividade coletiva, procedimento ou vacinacao. A NT30 preve ponderacao por vulnerabilidade (idoso/crianca x1.2, PBF/BPC x1.3, ambos x2.5).',
        calculo: 'Cidadaos com 1+ contato em 12m / total vinculados a eSF/eAP',
        periodo: 'Ultimos 12 meses',
        peso: 70,
        fontes: ['tb_fat_atendimento_individual', 'tb_fat_atendimento_odonto', 'tb_fat_visita_domiciliar', 'tb_fat_proced_atend', 'tb_fat_vacinacao'],
        codigos: [],
      },
    ],
  },

  'emulti': {
    eixo: 'emulti',
    nome: 'eMulti - Equipe Multiprofissional',
    descricao: 'Indicadores de desempenho das Equipes Multiprofissionais (tipo CNES 72). Avalia a produtividade e a atuacao interprofissional, medindo a media de atendimentos por pessoa cadastrada e o percentual de acoes compartilhadas entre profissionais.',
    tipo: 'taxa',
    populacao: 'Cidadaos vinculados a equipes do tipo eMulti (CNES 72).',
    scoreTotal: 100,
    classificacao: [
      { label: 'Otimo', cor: '#2e7d32', faixa: 'Score acima de 75' },
      { label: 'Bom', cor: '#1565c0', faixa: 'Score entre 50 e 75' },
      { label: 'Suficiente', cor: '#e65100', faixa: 'Score entre 25 e 50' },
      { label: 'Regular', cor: '#c62828', faixa: 'Score abaixo de 25' },
    ],
    indicadores: [
      {
        chave: 'A',
        nome: 'M1 - Media atendimentos/pessoa',
        descricao: 'Numero de atendimentos individuais e coletivos realizados pela eMulti no quadrimestre, expresso como media por pessoa atendida.',
        calculo: 'Atendimentos individuais e coletivos / pessoas atendidas pela eMulti',
        periodo: 'Quadrimestre (4 meses)',
        peso: 50,
        fontes: ['tb_fat_atendimento_individual', 'tb_dim_equipe'],
        codigos: [],
      },
      {
        chave: 'B',
        nome: 'M2 - Acoes interprofissionais (%)',
        descricao: 'Percentual de atendimentos registrados como acao compartilhada/interprofissional em relacao ao total de atendimentos. Indica o nivel de integracao entre os profissionais da equipe.',
        calculo: 'Acoes interprofissionais / total de atendimentos x 100',
        periodo: 'Quadrimestre (4 meses)',
        peso: 50,
        fontes: ['tb_fat_atendimento_individual'],
        codigos: [],
      },
    ],
  },

  'eapp-acesso': {
    eixo: 'eapp-acesso',
    nome: 'eAPP - Mais Acesso (Prisional)',
    descricao: 'Indicador de acesso para Equipes de Atencao Primaria Prisional (eAPP, tipo CNES 74). Avalia o equilibrio entre atendimentos programados e espontaneos, da mesma forma que o C1, porem apenas para equipes prisionais.',
    tipo: 'taxa',
    populacao: 'Atendimentos individuais realizados por equipes eAPP (tipo CNES 74).',
    scoreTotal: 100,
    classificacao: classificacaoC1,
    indicadores: [
      {
        chave: 'A',
        nome: '% Atendimentos Programados (eAPP)',
        descricao: 'Percentual de atendimentos programados sobre o total de atendimentos realizados pelas equipes de atencao primaria prisional.',
        calculo: 'Atendimentos programados / total de atendimentos x 100',
        periodo: 'Quadrimestre (4 meses)',
        peso: 100,
        fontes: ['tb_fat_atendimento_individual', 'tb_dim_tipo_atendimento'],
        codigos: [],
      },
    ],
  },

  'eapp-gestacao': {
    eixo: 'eapp-gestacao',
    nome: 'eAPP - Gestacao (Prisional)',
    descricao: 'Cuidado na gestacao para populacao prisional (eAPP). Avalia 5 boas praticas de acompanhamento pre-natal adaptadas ao contexto prisional.',
    tipo: 'boas-praticas',
    populacao: 'Gestantes vinculadas a equipes eAPP (tipo CNES 74).',
    scoreTotal: 100,
    classificacao: classificacaoPadrao,
    indicadores: [
      { chave: 'A', nome: '3+ consultas medico/enfermeiro', descricao: 'Gestante com pelo menos 3 consultas pre-natal com medico ou enfermeiro.', calculo: 'Gestantes com 3+ consultas distintas', periodo: 'Ultimos 12 meses', peso: 40, fontes: ['tb_fat_atendimento_individual'], codigos: [] },
      { chave: 'B', nome: 'Testes rapidos (HIV/Sifilis/HepB/HepC)', descricao: 'Gestante que realizou os 4 testes rapidos obrigatorios durante a gestacao.', calculo: 'Gestantes com todos os 4 testes rapidos realizados', periodo: 'Ultimos 12 meses', peso: 20, fontes: ['tb_fat_proced_atend'], codigos: [{ codigo: '0214010082', descricao: 'Teste rapido HIV' }, { codigo: '0214010074', descricao: 'Teste rapido Sifilis' }, { codigo: '0214010058', descricao: 'Teste rapido Hepatite B' }, { codigo: '0214010066', descricao: 'Teste rapido Hepatite C' }] },
      { chave: 'C', nome: 'Vacina dTpa (20a semana)', descricao: 'Gestante vacinada com dTpa a partir da 20a semana de gestacao.', calculo: 'Gestantes com vacina dTpa na gestacao', periodo: 'Ultimos 12 meses', peso: 15, fontes: ['tb_fat_vacinacao'], codigos: [{ codigo: 'Imuno 57', descricao: 'dTpa adulto' }] },
      { chave: 'D', nome: '3+ afericoes PA', descricao: 'Gestante com pelo menos 3 afericoes de pressao arterial durante a gestacao.', calculo: 'Gestantes com 3+ afericoes de PA', periodo: 'Ultimos 12 meses', peso: 15, fontes: ['tb_fat_proced_atend'], codigos: [{ codigo: '0301100039', descricao: 'Afericao de PA' }] },
      { chave: 'E', nome: 'Atividade saude bucal', descricao: 'Gestante com pelo menos 1 atendimento odontologico durante a gestacao.', calculo: 'Gestantes com atendimento odontologico', periodo: 'Ultimos 12 meses', peso: 10, fontes: ['tb_fat_atendimento_odonto'], codigos: [] },
    ],
  },

  'eapp-diabetes-has': {
    eixo: 'eapp-diabetes-has',
    nome: 'eAPP - Diabetes e Hipertensao (Prisional)',
    descricao: 'Cuidado de pessoas com diabetes e/ou hipertensao na populacao prisional (eAPP). Avalia consultas, afericao de PA, antropometria e exame de HbA1c.',
    tipo: 'boas-praticas',
    populacao: 'Cidadaos com diabetes e/ou hipertensao vinculados a equipes eAPP.',
    scoreTotal: 100,
    classificacao: classificacaoPadrao,
    indicadores: [
      { chave: 'A', nome: 'Consulta medico/enfermeiro (6m)', descricao: 'Paciente com consulta nos ultimos 6 meses.', calculo: 'Pacientes DM/HAS com consulta recente', periodo: 'Ultimos 6 meses', peso: 50, fontes: ['tb_fat_atendimento_individual'], codigos: [] },
      { chave: 'B', nome: 'Afericao PA (6m)', descricao: 'Paciente com afericao de pressao arterial nos ultimos 6 meses.', calculo: 'Pacientes com PA aferida', periodo: 'Ultimos 6 meses', peso: 20, fontes: ['tb_fat_proced_atend'], codigos: [{ codigo: '0301100039', descricao: 'Afericao de PA' }] },
      { chave: 'C', nome: 'Peso e altura (12m)', descricao: 'Paciente com registro de peso e altura nos ultimos 12 meses.', calculo: 'Pacientes com antropometria', periodo: 'Ultimos 12 meses', peso: 15, fontes: ['tb_fat_atendimento_individual', 'tb_fat_proced_atend'], codigos: [] },
      { chave: 'D', nome: 'HbA1c solicitada/avaliada (12m)', descricao: 'Paciente diabetico com hemoglobina glicada solicitada ou avaliada. Aplica-se somente a pacientes com diabetes (nao hipertensos isolados). Hipertensos sem diabetes recebem pontuacao automatica neste indicador.', calculo: 'Pacientes diabeticos com HbA1c nos ultimos 12 meses', periodo: 'Ultimos 12 meses', peso: 15, fontes: ['tb_fat_atd_ind_procedimentos'], codigos: [{ codigo: 'ABEX008', descricao: 'HbA1c (APS)' }, { codigo: '0202010503', descricao: 'Dosagem de HbA1c' }] },
    ],
  },

  'eapp-ist': {
    eixo: 'eapp-ist',
    nome: 'eAPP - Rastreio IST (Prisional)',
    descricao: 'Rastreamento de Infeccoes Sexualmente Transmissiveis na populacao prisional. Avalia a realizacao dos 4 testes rapidos obrigatorios: HIV, Sifilis, Hepatite B e Hepatite C.',
    tipo: 'boas-praticas',
    populacao: 'Cidadaos vinculados a equipes eAPP (tipo CNES 74).',
    scoreTotal: 100,
    classificacao: classificacaoPadrao,
    indicadores: [
      { chave: 'A', nome: 'Teste rapido HIV (12m)', descricao: 'Cidadao com teste rapido para HIV nos ultimos 12 meses.', calculo: 'Cidadaos testados / total', periodo: 'Ultimos 12 meses', peso: 25, fontes: ['tb_fat_proced_atend'], codigos: [{ codigo: '0214010082', descricao: 'Teste rapido HIV' }, { codigo: 'ABPG024', descricao: 'Teste rapido HIV (APS)' }] },
      { chave: 'B', nome: 'Teste rapido Sifilis (12m)', descricao: 'Cidadao com teste rapido para Sifilis nos ultimos 12 meses.', calculo: 'Cidadaos testados / total', periodo: 'Ultimos 12 meses', peso: 25, fontes: ['tb_fat_proced_atend'], codigos: [{ codigo: '0214010074', descricao: 'Teste rapido Sifilis' }, { codigo: 'ABPG028', descricao: 'Teste rapido Sifilis (APS)' }] },
      { chave: 'C', nome: 'Teste rapido Hepatite B (12m)', descricao: 'Cidadao com teste rapido para Hepatite B nos ultimos 12 meses.', calculo: 'Cidadaos testados / total', periodo: 'Ultimos 12 meses', peso: 25, fontes: ['tb_fat_proced_atend'], codigos: [{ codigo: '0214010058', descricao: 'Teste rapido HBsAg' }, { codigo: 'ABPG025', descricao: 'Teste rapido HepB (APS)' }] },
      { chave: 'D', nome: 'Teste rapido Hepatite C (12m)', descricao: 'Cidadao com teste rapido para Hepatite C nos ultimos 12 meses.', calculo: 'Cidadaos testados / total', periodo: 'Ultimos 12 meses', peso: 25, fontes: ['tb_fat_proced_atend'], codigos: [{ codigo: '0214010066', descricao: 'Teste rapido anti-HCV' }, { codigo: 'ABPG026', descricao: 'Teste rapido HepC (APS)' }] },
    ],
  },

  'eapp-cancer': {
    eixo: 'eapp-cancer',
    nome: 'eAPP - Cancer de Colo (Prisional)',
    descricao: 'Rastreamento de cancer de colo de utero na populacao prisional feminina. Avalia a realizacao de exame citopatologico em mulheres de 25 a 64 anos.',
    tipo: 'boas-praticas',
    populacao: 'Mulheres de 25 a 64 anos vinculadas a equipes eAPP (tipo CNES 74).',
    scoreTotal: 100,
    classificacao: classificacaoPadrao,
    indicadores: [
      { chave: 'A', nome: 'Citopatologico (36m) - mulheres 25-64', descricao: 'Mulher com exame citopatologico (Papanicolaou) nos ultimos 36 meses.', calculo: 'Mulheres com citopatologico / total de mulheres 25-64', periodo: 'Ultimos 36 meses', peso: 100, fontes: ['tb_fat_proced_atend'], codigos: [{ codigo: '0203010086', descricao: 'Citopatologico cervico-vaginal' }, { codigo: 'ABEX001', descricao: 'Citopatologico (APS)' }] },
    ],
  },

  'ecr-acesso': {
    eixo: 'ecr-acesso',
    nome: 'eCR - Mais Acesso (Consultorio na Rua)',
    descricao: 'Indicador de acesso para Equipes de Consultorio na Rua (eCR, tipo CNES 73). Avalia o equilibrio entre atendimentos programados e espontaneos para populacao em situacao de rua.',
    tipo: 'taxa',
    populacao: 'Atendimentos realizados por equipes eCR (tipo CNES 73).',
    scoreTotal: 100,
    classificacao: classificacaoC1,
    indicadores: [
      { chave: 'A', nome: '% Atendimentos Programados (eCR)', descricao: 'Percentual de atendimentos programados sobre o total de atendimentos das equipes de consultorio na rua.', calculo: 'Atendimentos programados / total x 100', periodo: 'Quadrimestre (4 meses)', peso: 100, fontes: ['tb_fat_atendimento_individual'], codigos: [] },
    ],
  },

  'ecr-gestacao': {
    eixo: 'ecr-gestacao',
    nome: 'eCR - Gestacao (Consultorio na Rua)',
    descricao: 'Cuidado na gestacao para populacao em situacao de rua. Avalia 5 boas praticas de acompanhamento pre-natal.',
    tipo: 'boas-praticas',
    populacao: 'Gestantes vinculadas a equipes eCR (tipo CNES 73).',
    scoreTotal: 100,
    classificacao: classificacaoPadrao,
    indicadores: [
      { chave: 'A', nome: '3+ consultas medico/enfermeiro', descricao: 'Gestante com pelo menos 3 consultas pre-natal.', calculo: '3+ consultas distintas', periodo: 'Ultimos 12 meses', peso: 30, fontes: ['tb_fat_atendimento_individual'], codigos: [] },
      { chave: 'B', nome: 'Testes rapidos (HIV/Sifilis/HepB/HepC)', descricao: 'Gestante com os 4 testes rapidos obrigatorios.', calculo: 'Todos os 4 testes realizados', periodo: 'Ultimos 12 meses', peso: 20, fontes: ['tb_fat_proced_atend'], codigos: [] },
      { chave: 'C', nome: 'Vacina dTpa (20a semana)', descricao: 'Gestante vacinada com dTpa na gestacao.', calculo: 'Gestantes com dTpa', periodo: 'Ultimos 12 meses', peso: 20, fontes: ['tb_fat_vacinacao'], codigos: [{ codigo: 'Imuno 57', descricao: 'dTpa adulto' }] },
      { chave: 'D', nome: '3+ afericoes PA', descricao: 'Gestante com 3+ afericoes de PA.', calculo: '3+ registros de PA', periodo: 'Ultimos 12 meses', peso: 20, fontes: ['tb_fat_proced_atend'], codigos: [] },
      { chave: 'E', nome: 'Atividade saude bucal', descricao: 'Gestante com atendimento odontologico.', calculo: '1+ atendimento odonto', periodo: 'Ultimos 12 meses', peso: 10, fontes: ['tb_fat_atendimento_odonto'], codigos: [] },
    ],
  },

  'ecr-ist': {
    eixo: 'ecr-ist',
    nome: 'eCR - Rastreio IST (Consultorio na Rua)',
    descricao: 'Rastreamento de IST para populacao em situacao de rua. Avalia a realizacao dos testes rapidos: HIV, Sifilis, Hepatite B e Hepatite C.',
    tipo: 'boas-praticas',
    populacao: 'Cidadaos vinculados a equipes eCR (tipo CNES 73).',
    scoreTotal: 100,
    classificacao: classificacaoPadrao,
    indicadores: [
      { chave: 'A', nome: 'Teste rapido HIV (12m)', descricao: 'Teste rapido HIV nos ultimos 12 meses.', calculo: 'Cidadaos testados / total', periodo: 'Ultimos 12 meses', peso: 25, fontes: ['tb_fat_proced_atend'], codigos: [{ codigo: '0214010082', descricao: 'Teste rapido HIV' }] },
      { chave: 'B', nome: 'Teste rapido Sifilis (12m)', descricao: 'Teste rapido Sifilis nos ultimos 12 meses.', calculo: 'Cidadaos testados / total', periodo: 'Ultimos 12 meses', peso: 25, fontes: ['tb_fat_proced_atend'], codigos: [{ codigo: '0214010074', descricao: 'Teste rapido Sifilis' }] },
      { chave: 'C', nome: 'Teste rapido Hepatite B (12m)', descricao: 'Teste rapido HepB nos ultimos 12 meses.', calculo: 'Cidadaos testados / total', periodo: 'Ultimos 12 meses', peso: 25, fontes: ['tb_fat_proced_atend'], codigos: [{ codigo: '0214010058', descricao: 'Teste rapido HBsAg' }] },
      { chave: 'D', nome: 'Teste rapido Hepatite C (12m)', descricao: 'Teste rapido HepC nos ultimos 12 meses.', calculo: 'Cidadaos testados / total', periodo: 'Ultimos 12 meses', peso: 25, fontes: ['tb_fat_proced_atend'], codigos: [{ codigo: '0214010066', descricao: 'Teste rapido anti-HCV' }] },
    ],
  },

  'ecr-tuberculose': {
    eixo: 'ecr-tuberculose',
    nome: 'eCR - Tuberculose (Consultorio na Rua)',
    descricao: 'Cuidado de pessoas com tuberculose na populacao em situacao de rua. Avalia consulta medica, baciloscopia, tratamento DOT e visita domiciliar.',
    tipo: 'boas-praticas',
    populacao: 'Cidadaos com tuberculose (CIAP A70) vinculados a equipes eCR (tipo CNES 73).',
    scoreTotal: 100,
    classificacao: classificacaoPadrao,
    indicadores: [
      { chave: 'A', nome: 'Consulta medica/enfermagem (12m)', descricao: 'Pessoa com TB com consulta nos ultimos 12 meses.', calculo: 'Pacientes TB com consulta recente', periodo: 'Ultimos 12 meses', peso: 25, fontes: ['tb_fat_atendimento_individual'], codigos: [{ codigo: 'CIAP A70', descricao: 'Tuberculose' }] },
      { chave: 'B', nome: 'Baciloscopia de escarro (12m)', descricao: 'Pessoa com TB com baciloscopia realizada nos ultimos 12 meses.', calculo: 'Pacientes com baciloscopia', periodo: 'Ultimos 12 meses', peso: 25, fontes: ['tb_fat_proced_atend'], codigos: [{ codigo: '0213010100', descricao: 'Baciloscopia direta de escarro' }, { codigo: 'ABPG029', descricao: 'Baciloscopia (APS)' }] },
      { chave: 'C', nome: 'Tratamento DOT registrado', descricao: 'Pessoa com TB com tratamento diretamente observado (DOT) registrado.', calculo: 'Pacientes com DOT nos ultimos 12 meses', periodo: 'Ultimos 12 meses', peso: 25, fontes: ['tb_fat_proced_atend'], codigos: [{ codigo: '0301100152', descricao: 'Tratamento diretamente observado (DOT)' }, { codigo: 'ABPG038', descricao: 'DOT (APS)' }] },
      { chave: 'D', nome: 'Visita domiciliar (12m)', descricao: 'Pessoa com TB com visita domiciliar nos ultimos 12 meses.', calculo: 'Pacientes com visita domiciliar', periodo: 'Ultimos 12 meses', peso: 25, fontes: ['tb_fat_visita_domiciliar'], codigos: [] },
    ],
  },

  'eapp-tuberculose': {
    eixo: 'eapp-tuberculose',
    nome: 'Tuberculose (Prisional)',
    descricao: 'Cuidado da pessoa com tuberculose em unidade prisional (eAPP)',
    tipo: 'boas-praticas',
    populacao: 'Pessoas com tuberculose (CIAP A70) vinculadas a equipes eAPP',
    scoreTotal: 100,
    classificacao: classificacaoPadrao,
    indicadores: [
      { chave: 'A', nome: 'Consulta medica/enfermagem (12m)', descricao: 'Pessoa com TB com consulta medica ou de enfermagem nos ultimos 12 meses, em equipes prisionais (tipo 74).', calculo: 'Pacientes TB com consulta recente', periodo: 'Ultimos 12 meses', peso: 25, fontes: ['tb_fat_atendimento_individual'], codigos: [{ codigo: 'CIAP A70', descricao: 'Tuberculose' }] },
      { chave: 'B', nome: 'Baciloscopia de escarro (12m)', descricao: 'Pessoa com TB com baciloscopia realizada nos ultimos 12 meses, em equipes prisionais (tipo 74).', calculo: 'Pacientes com baciloscopia', periodo: 'Ultimos 12 meses', peso: 25, fontes: ['tb_fat_proced_atend'], codigos: [{ codigo: '0213010100', descricao: 'Baciloscopia direta de escarro' }, { codigo: 'ABPG029', descricao: 'Baciloscopia (APS)' }] },
      { chave: 'C', nome: 'Tratamento DOT registrado', descricao: 'Pessoa com TB com tratamento diretamente observado (DOT) registrado, em equipes prisionais (tipo 74).', calculo: 'Pacientes com DOT nos ultimos 12 meses', periodo: 'Ultimos 12 meses', peso: 25, fontes: ['tb_fat_proced_atend'], codigos: [{ codigo: '0301100152', descricao: 'Tratamento diretamente observado (DOT)' }, { codigo: 'ABPG038', descricao: 'DOT (APS)' }] },
      { chave: 'D', nome: 'Visita domiciliar (12m)', descricao: 'Pessoa com TB com visita domiciliar nos ultimos 12 meses, em equipes prisionais (tipo 74).', calculo: 'Pacientes com visita domiciliar', periodo: 'Ultimos 12 meses', peso: 25, fontes: ['tb_fat_visita_domiciliar'], codigos: [] },
    ],
  },
};

// eSB B1-B6 como eixos individuais: reaproveitam a descricao do indicador correspondente
// do eixo consolidado 'odonto', mas com populacao-alvo e faixas de classificacao proprias
// por indicador (fieis as Notas Metodologicas B1-B6 do MS e as BANDAS do backend).
// ocultarScore=true: sao indicadores de taxa/razao, sem score de pontuacao 0-100.
const ESB_EIXO_META: Record<string, { chave: string; populacao: string; classificacao: ClassificacaoFaixa[] }> = {
  'b1-primeira-consulta': {
    chave: 'A',
    populacao: 'Pessoas vinculadas as eSF/eAP de referencia da eSB (regra de vinculacao da Nota Tecnica 30/2025). Numerador: primeiras consultas programaticas do cirurgiao-dentista (CBO 2232-08/93/72).',
    classificacao: [
      { label: 'Otimo', cor: '#2e7d32', faixa: '> 1,25' },
      { label: 'Bom', cor: '#1565c0', faixa: '> 0,75 e <= 1,25' },
      { label: 'Suficiente', cor: '#e65100', faixa: '> 0,25 e <= 0,75' },
      { label: 'Regular', cor: '#c62828', faixa: '<= 0,25' },
    ],
  },
  'b2-tratamento-concluido': {
    chave: 'B',
    populacao: 'Pessoas com primeira consulta odontologica programatica realizada pela eSB (mesmo universo do numerador do B1). Considera apenas o cirurgiao-dentista (CBO 2232-08/93/72).',
    classificacao: [
      { label: 'Otimo', cor: '#2e7d32', faixa: '> 75%' },
      { label: 'Bom', cor: '#1565c0', faixa: '> 50% e <= 75%' },
      { label: 'Suficiente', cor: '#e65100', faixa: '> 25% e <= 50%' },
      { label: 'Regular', cor: '#c62828', faixa: '<= 25%' },
    ],
  },
  'b3-exodontia': {
    chave: 'C',
    populacao: 'Procedimentos individuais preventivos, curativos e exodontias realizados pela eSB. Numerador (exodontias): cirurgiao-dentista; denominador: cirurgiao-dentista e tecnico em saude bucal (CBO 3224-05/25).',
    classificacao: [
      { label: 'Otimo', cor: '#2e7d32', faixa: '>= 3% e < 10%' },
      { label: 'Bom', cor: '#1565c0', faixa: '>= 10% e < 12%' },
      { label: 'Suficiente', cor: '#e65100', faixa: '>= 12% e < 14%' },
      { label: 'Regular', cor: '#c62828', faixa: '< 3% ou >= 14%' },
    ],
  },
  'b4-escovacao': {
    chave: 'D',
    populacao: 'Criancas de 6 a 12 anos vinculadas as eSF/eAP de referencia da eSB (regra da Nota Tecnica 30/2025). Acao coletiva realizada por CD, TSB ou ASB.',
    classificacao: [
      { label: 'Otimo', cor: '#2e7d32', faixa: '> 1' },
      { label: 'Bom', cor: '#1565c0', faixa: '> 0,5 e <= 1' },
      { label: 'Suficiente', cor: '#e65100', faixa: '> 0,25 e <= 0,5' },
      { label: 'Regular', cor: '#c62828', faixa: '<= 0,25' },
    ],
  },
  'b5-preventivos': {
    chave: 'E',
    populacao: 'Procedimentos odontologicos individuais realizados pela eSB (cirurgiao-dentista e tecnico em saude bucal: CBO 2232-08/93/72 e 3224-05/25).',
    classificacao: [
      { label: 'Otimo', cor: '#2e7d32', faixa: '>= 65% e <= 85%' },
      { label: 'Bom', cor: '#1565c0', faixa: '>= 55% e < 65%' },
      { label: 'Suficiente', cor: '#e65100', faixa: '>= 40% e < 55%' },
      { label: 'Regular', cor: '#c62828', faixa: '< 40% ou > 85%' },
    ],
  },
  'b6-tra-art': {
    chave: 'F',
    populacao: 'Procedimentos restauradores realizados pela eSB. Considera apenas o cirurgiao-dentista (CBO 2232-08/93/72); exclui restauracoes com amalgama.',
    classificacao: [
      { label: 'Otimo', cor: '#2e7d32', faixa: '> 8%' },
      { label: 'Bom', cor: '#1565c0', faixa: '> 6% e <= 8%' },
      { label: 'Suficiente', cor: '#e65100', faixa: '> 3% e <= 6%' },
      { label: 'Regular', cor: '#c62828', faixa: '<= 3%' },
    ],
  },
};
const odontoHelp = INDICADORES_HELP['odonto'];
if (odontoHelp) {
  for (const [slug, meta] of Object.entries(ESB_EIXO_META)) {
    const ind = odontoHelp.indicadores.find((i) => i.chave === meta.chave);
    if (!ind) continue;
    INDICADORES_HELP[slug] = {
      eixo: slug,
      nome: ind.nome,
      descricao: ind.descricao,
      tipo: 'taxa',
      populacao: meta.populacao,
      scoreTotal: 100,
      classificacao: meta.classificacao,
      indicadores: [{ ...ind, peso: 100 }],
      ocultarScore: true,
    };
  }
}

// eMulti M1/M2 como eixos individuais: reaproveitam o indicador correspondente do
// eixo consolidado 'emulti', mas com populacao-alvo e faixas de classificacao proprias
// (o consolidado usa faixas genericas de score 0-100, que nao valem para M1/M2 isolados).
// Faixas alinhadas ao backend emulti.queries.ts BANDAS (M1 media/ratio, M2 percentual).
const EMULTI_EIXO_META: Record<string, { chave: string; populacao: string; classificacao: ClassificacaoFaixa[] }> = {
  'm1-media-atendimentos': {
    chave: 'A',
    populacao: 'Pessoas atendidas pela eMulti (atendimento individual ou atividade coletiva).',
    classificacao: [
      { label: 'Otimo', cor: '#2e7d32', faixa: 'Media > 3' },
      { label: 'Bom', cor: '#1565c0', faixa: 'Media > 2 e <= 3' },
      { label: 'Suficiente', cor: '#e65100', faixa: 'Media > 1 e <= 2' },
      { label: 'Regular', cor: '#c62828', faixa: 'Media <= 1' },
    ],
  },
  'm2-acoes-interprofissionais': {
    chave: 'B',
    populacao: 'Pessoas atendidas pela eMulti (atendimento individual ou atividade coletiva).',
    classificacao: [
      { label: 'Otimo', cor: '#2e7d32', faixa: '> 5%' },
      { label: 'Bom', cor: '#1565c0', faixa: '> 2,5% e <= 5%' },
      { label: 'Suficiente', cor: '#e65100', faixa: '> 1% e <= 2,5%' },
      { label: 'Regular', cor: '#c62828', faixa: '<= 1%' },
    ],
  },
};
const emultiHelp = INDICADORES_HELP['emulti'];
if (emultiHelp) {
  for (const [slug, meta] of Object.entries(EMULTI_EIXO_META)) {
    const ind = emultiHelp.indicadores.find((i) => i.chave === meta.chave);
    if (!ind) continue;
    INDICADORES_HELP[slug] = {
      eixo: slug,
      nome: ind.nome,
      descricao: ind.descricao,
      tipo: 'taxa',
      populacao: meta.populacao,
      scoreTotal: 100,
      classificacao: meta.classificacao,
      indicadores: [{ ...ind, peso: 100 }],
    };
  }
}

/** Retorna os dados de ajuda de um eixo */
export function getEixoHelp(eixo: string): EixoHelp | undefined {
  return INDICADORES_HELP[eixo];
}

/** Retorna os dados de ajuda de um indicador especifico */
export function getIndicadorHelp(eixo: string, chave: string): IndicadorHelp | undefined {
  const eixoData = INDICADORES_HELP[eixo];
  if (!eixoData) return undefined;
  return eixoData.indicadores.find((i) => i.chave === chave);
}
